import React, { useState, useEffect, useRef } from 'react';
import './ChatWidget.css';
import { sendMessageToAPI, sendLeadEscalation } from './chatService';

// ============================================================================
// CONFIGURATION - Update these values
// ============================================================================
const CONFIG = {
  branding: {
    name: 'eHAAZRI AI Assistant',
    greeting: 'Hello 👋',
    subtitle: 'How can I help you today?',
    placeholder: 'Ask AI anything...',
    disclaimer: 'AI can make mistakes.'
  },
  images: {
    chatIcon: 'https://ict-technologies.com/wp-content/uploads/2025/11/stars-2.png',
    aiAvatar: 'https://ict-technologies.com/wp-content/uploads/2025/11/stars__1_-removebg-preview.png',
    sendIcon: 'https://ict-technologies.com/wp-content/uploads/2025/11/send.png',
    hideIcon: 'https://ict-technologies.com/wp-content/uploads/2025/11/door-1.png',
    arrowIcon: 'https://ict-technologies.com/wp-content/uploads/2025/11/up-right-arrow.png'
  },
  quickQuestions: [
    "What is eHAAZRI?",
    "What are eHAAZRI's best features?",
    "Is eHAAZRI free or paid?",
    "What are the pricing details?",
    "How do I proceed further?"
  ]
};

const ChatWidget = () => {
  // State management
  const [isOpen, setIsOpen] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadFormSubmitted, setLeadFormSubmitted] = useState(false);
  const [inputDisabled, setInputDisabled] = useState(false);
  
  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Initialize session ID on mount
  useEffect(() => {
    const storedSessionId = sessionStorage.getItem('chatSessionId');
    if (storedSessionId) {
      setSessionId(storedSessionId);
    } else {
      const newSessionId = `chat-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      sessionStorage.setItem('chatSessionId', newSessionId);
      setSessionId(newSessionId);
    }
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping, showLeadForm]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current && !inputDisabled) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [isOpen, inputDisabled]);

  // ============================================================================
  // MESSAGE FORMATTING
  // ============================================================================

  const formatMessage = (text) => {
    // Step 1: Escape HTML to prevent injection
    let processedText = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    // Step 2: Apply formatting rules
    processedText = processedText
      // Bold text with **
      .replace(/\*\*([^*\n]+?)\*\*/g, (match, content) => {
        return `<strong style="color: #0d9488; font-weight: 800; font-size: 15px;">${content}</strong>`;
      })
      // Italic text with *
      .replace(/\*([^*\n]+?)\*/g, '<em style="font-style: italic; color: #374151;">$1</em>')
      // Inline code with `
      .replace(/`([^`]+?)`/g, '<code style="background: #f3f4f6; padding: 2px 6px; border-radius: 4px; font-family: monospace; font-size: 13px; color: #dc2626;">$1</code>')
      // Style emojis
      .replace(/(💰|📊|💵|ℹ️|🎉|🎯)/g, '<span style="font-size: 17px; margin-right: 4px; vertical-align: middle;">$1</span>')
      // Section headers ending with colon
      .replace(/^(.*?):(\s*)$/gm, (match, header) => {
        return `<div style="font-weight: 800; color: #0f766e; margin-top: 14px; margin-bottom: 8px; font-size: 15px;">${header}:</div>`;
      })
      // Bullet points
      .replace(/^[•\-\*]\s+(.+)$/gm, '<div style="display: flex; gap: 8px; margin: 6px 0; padding-left: 4px;"><span style="color: #0d9488; font-weight: bold; min-width: 12px;">•</span><span style="flex: 1;">$1</span></div>')
      // Numbered lists
      .replace(/^(\d+)\.\s+(.+)$/gm, '<div style="display: flex; gap: 8px; margin: 6px 0; padding-left: 4px;"><span style="color: #0d9488; font-weight: bold; min-width: 24px;">$1.</span><span style="flex: 1;">$2</span></div>')
      // Line breaks
      .replace(/\n\n+/g, '<br><br>')
      .replace(/\n/g, '<br>')
      // Multiple spaces to non-breaking spaces
      .replace(/  +/g, (match) => '&nbsp;'.repeat(match.length));
    
    return processedText;
  };

  // ============================================================================
  // MESSAGE HANDLING
  // ============================================================================

  const sendMessage = async (message) => {
    if (!message.trim()) return;

    // Hide welcome screen and show messages
    setShowWelcome(false);

    // Add user message to chat
    const userMessage = {
      type: 'user',
      text: message,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    try {
      const response = await sendMessageToAPI(message, sessionId);
      
      setIsTyping(false);

      // Check for fallback flag - show lead form if API fails
      if (response.forceFallback === true) {
        setShowLeadForm(true);
        setInputDisabled(true);
        if (inputRef.current) {
          inputRef.current.placeholder = "Please use the form above...";
        }
        return;
      }
      
      // Add agent response to chat
      const agentMessage = {
        type: 'agent',
        text: response.response || 'I received your message.',
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, agentMessage]);
      
    } catch (error) {
      console.error('Chat error:', error);
      setIsTyping(false);
      setShowLeadForm(true);
      setInputDisabled(true);
    }
  };

  // Handle quick question button click
  const handleQuickQuestion = (question) => {
    sendMessage(question);
  };

  // Handle textarea auto-resize
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    
    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    const maxHeight = 120;
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = newHeight + 'px';
  };

  // Handle Enter key press
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  // ============================================================================
  // LEAD FORM HANDLING
  // ============================================================================

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    
    const formData = new FormData(e.target);
    const payload = {
      name: formData.get('name'),
      email: formData.get('email'),
      reason: formData.get('reason'),
      sessionId: sessionId
    };

    const button = e.target.querySelector('button[type="submit"]');
    button.disabled = true;
    button.textContent = 'Sending...';

    try {
      const response = await sendLeadEscalation(payload);
      
      if (response.success) {
        setLeadFormSubmitted(true);
      } else {
        alert('Error sending request. Please try again.');
        button.disabled = false;
        button.textContent = 'Send Request';
      }
    } catch (error) {
      console.error('Lead submission error:', error);
      alert('Failed to send request. Please try again.');
      button.disabled = false;
      button.textContent = 'Send Request';
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="chat-widget-container">
      {/* ====================================================================
          CHAT TOGGLE BUTTON
          ==================================================================== */}
      <button
        className="chat-toggle-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="6" y1="18" x2="18" y2="6" />
          </svg>
        ) : (
          <>
            <img src={CONFIG.images.chatIcon} alt="AI Assistant" className="chat-icon" />
            <span className="chat-btn-text">Ask AI Assistant</span>
          </>
        )}
      </button>

      {/* ====================================================================
          CHAT PANEL
          ==================================================================== */}
      {isOpen && (
        <div className="chat-panel">
          
          {/* HEADER */}
          <div className="chat-header">
            <div className="chat-header-info">
              <img src={CONFIG.images.chatIcon} alt="AI" className="header-avatar" />
              <div className="header-text">
                <div className="header-title">{CONFIG.branding.name}</div>
                <div className="header-status">
                  <span className="status-dot" />
                  Online
                </div>
              </div>
            </div>
            
            {/* Hide Button with Tooltip */}
            <div style={{ position: 'relative' }}>
              <button 
                className="hide-chat-button"
                onClick={() => setIsOpen(false)}
                aria-label="Hide chat"
              >
                <img src={CONFIG.images.hideIcon} alt="Hide" />
                <div className="hide-tooltip">Hide</div>
              </button>
            </div>
          </div>

          {/* MAIN CONTENT AREA */}
          <div className="welcome-container">
            
            {/* ================================================================
                WELCOME SCREEN (Shows initially)
                ================================================================ */}
            {showWelcome && (
              <div className="welcome-view">
                <h3 className="welcome-greeting">{CONFIG.branding.greeting}</h3>
                <h4 className="welcome-subtitle">{CONFIG.branding.subtitle}</h4>
                
                {/* Quick Question Buttons */}
                <div className="quick-questions-container">
                  {CONFIG.quickQuestions.map((question, idx) => (
                    <button
                      key={idx}
                      className="quick-question-btn"
                      onClick={() => handleQuickQuestion(question)}
                    >
                      <img src={CONFIG.images.arrowIcon} alt="" />
                      <span>{question}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ================================================================
                MESSAGES AREA (Shows after first message)
                ================================================================ */}
            {!showWelcome && (
              <div className="chat-messages">
                
                {/* Chat Started Badge */}
                <div className="chat-started-badge">
                  <span>chat started</span>
                </div>
                
                {/* Message List */}
                {messages.map((msg, idx) => (
                  <div key={idx} className={`message-wrapper ${msg.type}`}>
                    
                    {/* Agent Message */}
                    {msg.type === 'agent' && (
                      <div className="agent-message-container">
                        <div className="agent-label">
                          <img src={CONFIG.images.aiAvatar} alt="AI" className="agent-avatar" />
                          <span className="agent-name">eHAAZRI</span>
                        </div>
                        <div 
                          className="message-bubble agent"
                          dangerouslySetInnerHTML={{ __html: formatMessage(msg.text) }}
                        />
                      </div>
                    )}
                    
                    {/* User Message */}
                    {msg.type === 'user' && (
                      <div className="message-bubble user">
                        {msg.text}
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Typing Indicator */}
                {isTyping && (
                  <div className="message-wrapper agent">
                    <div className="typing-bubble">
                      <div className="typing-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2L14.4 9.6L22 12L14.4 14.4L12 22L9.6 14.4L2 12L9.6 9.6L12 2Z"/>
                        </svg>
                      </div>
                      <span>Thinking...</span>
                    </div>
                  </div>
                )}

                {/* ============================================================
                    LEAD FORM (Shows when API fails)
                    ============================================================ */}
                {showLeadForm && !leadFormSubmitted && (
                  <div className="lead-form-container">
                    <div className="lead-form-header">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      Connection Issue
                    </div>
                    <p className="lead-form-message">
                      I apologize, I'm having trouble connecting to the system. Please leave your details and an admin will contact you shortly.
                    </p>
                    
                    <form className="lead-form" onSubmit={handleLeadSubmit}>
                      <input 
                        type="text" 
                        name="name" 
                        placeholder="Your Name" 
                        required 
                        autoComplete="name"
                      />
                      <input 
                        type="email" 
                        name="email" 
                        placeholder="Your Email" 
                        required 
                        autoComplete="email"
                      />
                      <textarea 
                        name="reason" 
                        placeholder="How can we help?" 
                        required 
                        rows="3"
                      />
                      <button type="submit">Send Request</button>
                    </form>
                  </div>
                )}

                {/* Lead Form Success Message */}
                {leadFormSubmitted && (
                  <div className="lead-success">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    <div className="lead-success-title">Request Sent</div>
                    <div className="lead-success-message">
                      An admin will reply to your email shortly.
                    </div>
                  </div>
                )}
                
                {/* Scroll anchor */}
                <div ref={messagesEndRef} />
              </div>
            )}

            {/* ================================================================
                INPUT AREA (Always visible at bottom)
                ================================================================ */}
            <div className="chat-input-container">
              <div className="input-wrapper">
                <textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder={CONFIG.branding.placeholder}
                  className="chat-input"
                  disabled={inputDisabled}
                  aria-label="Type your message"
                />
                <button
                  className={`send-btn ${inputValue.trim() && !inputDisabled ? 'active' : ''}`}
                  onClick={() => sendMessage(inputValue)}
                  disabled={!inputValue.trim() || inputDisabled}
                  aria-label="Send message"
                >
                  <img src={CONFIG.images.sendIcon} alt="" />
                </button>
              </div>
              
              <div className="disclaimer">
                <span>{CONFIG.branding.disclaimer}</span>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;